import {
  Cartesian2,
  Cartesian3,
  defined,
  destroyObject,
  IntersectionTests,
  Matrix4,
  Plane,
  Ray,
  Scene,
} from "cesium";
import AxisLinePrimitive from "components/elements/CesiumViewer/AxisLinePrimitive";
import getScreenSpaceScalingMatrix from "components/elements/CesiumViewer/core/utils/getScreenSpaceScalingMatrix";
import getWidgetOrigin from "components/elements/CesiumViewer/getWidgetOrigin";
import TransformAxis from "components/elements/CesiumViewer/TransformAxis";

const widgetOriginScratch = new Cartesian3();
const originScratch = new Cartesian3();
const directionScratch = new Cartesian3();
const planeNormalScratch = new Cartesian3();
const pickedPointScratch = new Cartesian3();
const moveScratch = new Cartesian3();
const offsetProjectedScratch = new Cartesian3();
const rayScratch = new Ray();

const PIXEL_SIZE = new Cartesian2(100, 100);
const MAXIMUM_SIZE_IN_METERS = new Cartesian2(Infinity, Infinity);

function getLinePrimitive(axis: string): AxisLinePrimitive {
  return new AxisLinePrimitive({
    positions: [Cartesian3.ZERO, (TransformAxis as any).getValue(axis)],
    arrow: true,
    color: (TransformAxis as any).getColor(axis),
    id: axis,
    show: false,
  });
}

/**
 * Options for constructing a TranslationEditor
 */
interface TranslationEditorOptions {
  /** The scene where the editor will be rendered */
  scene: Scene;
  /** The offset from the model origin to display the widget */
  originOffset: Cartesian3;
  /** Callback function to set the position when dragging */
  setPosition: (position: Cartesian3) => void;
  /** The transformation matrix of the object being edited */
  localToWorldTransform: Matrix4;
  /** The radius of the object */
  radius: number;
}

class TranslationEditor {
  private _polylineX: AxisLinePrimitive;
  private _polylineY: AxisLinePrimitive;
  private _polylineZ: AxisLinePrimitive;
  private _scene: Scene;
  private _setPositionCallback: (position: Cartesian3) => void;
  private _modelMatrix: Matrix4;
  private _dragAlongVector?: Cartesian3;
  private _offsetVector: Cartesian3;
  private _pickingPlane: Plane;
  private _dragging: boolean;
  private _active: boolean;
  private _localToWorldTransform: Matrix4;
  private _radius: number;

  originOffset: Cartesian3;

  constructor(options: TranslationEditorOptions) {
    const scene = options.scene;

    this.originOffset = options.originOffset;

    this._polylineX = scene.primitives.add(getLinePrimitive(TransformAxis.X));
    this._polylineY = scene.primitives.add(getLinePrimitive(TransformAxis.Y));
    this._polylineZ = scene.primitives.add(getLinePrimitive(TransformAxis.Z));

    this._scene = scene;
    this._setPositionCallback = options.setPosition;
    this._modelMatrix = new Matrix4();

    this._dragAlongVector = undefined;
    this._offsetVector = new Cartesian3();
    this._pickingPlane = new Plane(Cartesian3.UNIT_X, 0.0);
    this._dragging = false;
    this._active = false;

    this._localToWorldTransform = options.localToWorldTransform;
    this._radius = options.radius;

    this.update();
  }

  get active(): boolean {
    return this._active;
  }

  set active(active: boolean) {
    this._active = active;
    if (active) {
      this._polylineX.show = true;
      this._polylineY.show = true;
      this._polylineZ.show = true;
    } else {
      this._polylineX.show = false;
      this._polylineY.show = false;
      this._polylineZ.show = false;
      this._dragging = false;
    }
  }

  get localToWorldTransform(): Matrix4 {
    return this._localToWorldTransform;
  }

  set localToWorldTransform(value: Matrix4) {
    this._localToWorldTransform = value;
    this.update();
  }

  update(): void {
    const localToWorldTransform = this._localToWorldTransform;

    let modelMatrix = Matrix4.clone(localToWorldTransform, this._modelMatrix);

    const widgetOrigin = getWidgetOrigin(
      localToWorldTransform,
      this.originOffset,
      widgetOriginScratch,
    );

    modelMatrix = Matrix4.setTranslation(
      modelMatrix,
      widgetOrigin,
      modelMatrix,
    );

    const length =
      this._radius * Matrix4.getMaximumScale(this._localToWorldTransform) * 1.5;

    modelMatrix = Matrix4.multiplyByUniformScale(
      modelMatrix,
      length,
      modelMatrix,
    );

    modelMatrix = getScreenSpaceScalingMatrix(
      PIXEL_SIZE,
      MAXIMUM_SIZE_IN_METERS,
      (this._scene as any).frameState,
      modelMatrix,
      modelMatrix,
    );

    this._polylineX.modelMatrix = modelMatrix;
    this._polylineY.modelMatrix = modelMatrix;
    this._polylineZ.modelMatrix = modelMatrix;
  }

  handleLeftDown(position: Cartesian2): void {
    const scene = this._scene;
    const camera = scene.camera;

    const pickedObjects = scene.drillPick(position);

    let pickedAxis: string | undefined;
    for (let i = 0; i < pickedObjects.length; i++) {
      const object = pickedObjects[i];
      if (defined(object.id) && defined((TransformAxis as any)[object.id])) {
        pickedAxis = object.id;
        break;
      }
    }
    if (!defined(pickedAxis)) {
      return;
    }

    const origin = Matrix4.getTranslation(
      this._localToWorldTransform,
      originScratch,
    );
    const dragAlongVector = TransformAxis.getValue(pickedAxis);
    const directionVector = Matrix4.multiplyByPointAsVector(
      this._localToWorldTransform,
      dragAlongVector,
      directionScratch,
    );

    //Finds a picking plane that includes the dragged axis and is somewhat perpendicular to the camera
    let planeNormal = planeNormalScratch;
    if (Math.abs(Cartesian3.dot(camera.upWC, directionVector)) > 0.7) {
      // if up and the direction are close to parallel, the dot product will be close to 1
      planeNormal = Cartesian3.cross(
        camera.rightWC,
        directionVector,
        planeNormal,
      );
    } else {
      planeNormal = Cartesian3.cross(camera.upWC, directionVector, planeNormal);
    }
    Cartesian3.normalize(planeNormal, planeNormal);

    const pickingPlane = Plane.fromPointNormal(
      origin,
      planeNormal,
      this._pickingPlane,
    );
    const offsetVector = IntersectionTests.rayPlane(
      camera.getPickRay(position, rayScratch),
      pickingPlane,
      this._offsetVector,
    );
    if (!defined(offsetVector)) {
      return;
    }
    Cartesian3.subtract(offsetVector, origin, offsetVector);
    this._dragging = true;
    this._dragAlongVector = dragAlongVector;
    scene.screenSpaceCameraController.enableInputs = false;
  }

  handleMouseMove(position: Cartesian2): void {
    if (!this._dragging) {
      return;
    }
    const scene = this._scene;
    const camera = scene.camera;

    const pickedPoint = IntersectionTests.rayPlane(
      camera.getPickRay(position, rayScratch),
      this._pickingPlane,
      pickedPointScratch,
    );
    if (!defined(pickedPoint)) {
      return;
    }

    const dragAlongVector = this._dragAlongVector!;
    let origin = Matrix4.getTranslation(
      this._localToWorldTransform,
      originScratch,
    );
    const directionVector = Matrix4.multiplyByPointAsVector(
      this._localToWorldTransform,
      dragAlongVector,
      directionScratch,
    );
    let moveVector = Cartesian3.subtract(pickedPoint, origin, moveScratch);
    Cartesian3.projectVector(moveVector, directionVector, moveVector);
    const offset = Cartesian3.projectVector(
      this._offsetVector,
      directionVector,
      offsetProjectedScratch,
    );
    Cartesian3.subtract(moveVector, offset, moveVector);

    origin = Cartesian3.add(origin, moveVector, origin);
    this._setPositionCallback(origin);
  }

  handleLeftUp(): void {
    this._dragging = false;
    this._scene.screenSpaceCameraController.enableInputs = true;
  }

  destroy(): void {
    this.active = false;
    const scene = this._scene;
    scene?.primitives?.remove(this._polylineX);
    scene?.primitives?.remove(this._polylineY);
    scene?.primitives?.remove(this._polylineZ);
    destroyObject(this);
  }
}

export default TranslationEditor;
